import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Award,
  Database,
  ExternalLink,
  type LucideIcon,
  Rocket,
  Shield,
  Users,
  Zap,
} from "lucide-react";
import { BrandLogo, Numeric } from "@/shared/components";
import {
  CATEGORY_META,
  DOMAIN_META,
  DOMAIN_ORDER,
  TOOLS,
  countByCategory,
} from "@/shared/lib/tool-registry";

const DASHBOARD_PATH = "/dashboard";

/* ────────────── Navigation ────────────── */

export function LandingNav({ isAuthenticated }: { isAuthenticated: boolean }) {
  return (
    <div className="flex w-full items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <BrandLogo className="size-7" />
        <span className="text-[15px] font-bold tracking-tight">
          datalab.tools
        </span>
      </Link>
      <nav className="hidden items-center gap-8 text-[13px] font-medium text-text-secondary md:flex">
        <a href="#features" className="hover:text-text-primary">
          기능
        </a>
        <a href="#domains" className="hover:text-text-primary">
          분석 영역
        </a>
        <a href="#pricing" className="hover:text-text-primary">
          가격
        </a>
        <a href="#about" className="hover:text-text-primary">
          소개
        </a>
      </nav>
      <div className="flex items-center gap-2">
        {isAuthenticated ? (
          <Link
            to={DASHBOARD_PATH}
            className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-4 py-2 text-[13px] font-semibold text-text-inverse hover:bg-accent-hover"
          >
            대시보드 <ArrowRight className="size-3.5" />
          </Link>
        ) : (
          <>
            <Link
              to="/auth/login"
              className="rounded-sm px-3 py-2 text-[13px] font-medium text-text-secondary hover:text-text-primary"
            >
              로그인
            </Link>
            <Link
              to={DASHBOARD_PATH}
              className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-4 py-2 text-[13px] font-semibold text-text-inverse hover:bg-accent-hover"
            >
              시작하기 <ArrowRight className="size-3.5" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/* ────────────── Hero ────────────── */

const HERO_STATS = [
  { val: "42K+", label: "분석 키워드" },
  { val: "48개", label: "분석 기능" },
  { val: "99%", label: "서비스 안정성" },
];

export function HeroSection({ isAuthenticated }: { isAuthenticated: boolean }) {
  return (
    <section className="border-b border-stroke-subtle">
      <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
        <div className="inline-flex items-center gap-2 rounded-full border border-stroke-default px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-text-brand">
          <span className="size-1.5 rounded-full bg-accent" />
          Keyword Intelligence
        </div>
        <h1 className="mt-6 max-w-3xl text-[clamp(2rem,5vw,3.2rem)] font-extrabold leading-[1.15] tracking-tight text-text-primary">
          마케팅 데이터의
          <br />
          핵심을 꿰뚫는 <span className="text-text-brand">분석</span>
        </h1>
        <p className="mt-6 max-w-xl text-[15px] leading-[1.8] text-text-secondary">
          42,000개 키워드 × 7개 채널 유형 × 21개 AI 기능. 키워드 마케팅에 필요한
          모든 데이터를 수집·분석하고, 실행 가능한 전략까지 제안합니다.
        </p>

        <div className="mt-8 flex flex-wrap gap-6">
          {HERO_STATS.map((s) => (
            <div key={s.label}>
              <Numeric
                as="p"
                className="text-[22px] font-extrabold tracking-tight text-text-brand"
              >
                {s.val}
              </Numeric>
              <p className="text-[11px] font-medium text-text-tertiary">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to={DASHBOARD_PATH}
            className="group inline-flex items-center gap-2 rounded-sm bg-accent px-6 py-3 text-[14px] font-semibold text-text-inverse shadow-sm hover:bg-accent-hover"
          >
            {isAuthenticated ? "대시보드 이동" : "무료로 시작하기"}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <a
            href="#features"
            className="inline-flex items-center gap-2 rounded-sm border border-stroke-default px-6 py-3 text-[14px] font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
          >
            기능 살펴보기
          </a>
        </div>
      </div>
    </section>
  );
}

/* ────────────── Domains ────────────── */

export function DomainsSection() {
  return (
    <section id="domains" className="border-b border-stroke-subtle py-20">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="5대 분석 영역"
          title="키워드·채널·콘텐츠·광고·인사이트"
          description="데이터랩툴즈의 5대 분석 영역은 마케팅 전 과정을 커버합니다."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          {DOMAIN_ORDER.map((key) => {
            const meta = DOMAIN_META[key];
            return (
              <Link
                key={key}
                to={meta.basePath}
                className="group flex flex-col gap-3 rounded-sm border border-stroke-default bg-surface-raised p-5 transition-colors hover:border-accent"
              >
                <div className="flex size-10 items-center justify-center rounded-sm bg-accent-soft text-text-brand">
                  {meta.icon}
                </div>
                <h3 className="text-[15px] font-semibold text-text-primary group-hover:text-text-brand">
                  {meta.label}
                </h3>
                <p className="text-[12px] leading-relaxed text-text-tertiary">
                  {meta.desc}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ────────────── Features grid ────────────── */

export function FeaturesSection() {
  const counts = countByCategory();
  const smart = TOOLS.filter((t) => t.category === "smart").slice(0, 9);
  return (
    <section
      id="features"
      className="border-b border-stroke-subtle bg-surface-sunken py-20"
    >
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="AI 분석 기능"
          title="48개 통합 분석 도구"
          description={`실시간 AI 분석 ${counts.smart}개 · 대량 데이터 ${counts.data}개 · 자동화 ${counts.automation}개.`}
        />
        <div className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {smart.map((tool) => (
            <FeatureCard key={tool.to} tool={tool} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ tool }: { tool: (typeof TOOLS)[number] }) {
  const category = CATEGORY_META[tool.category];
  return (
    <Link
      to={tool.to}
      className="group flex flex-col gap-2 rounded-sm border border-stroke-default bg-surface-raised p-5 transition-colors hover:border-accent"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-brand">
          <span className={`size-1 rounded-full ${category.badgeDot}`} />
          {category.label}
        </span>
      </div>
      <h3 className="text-[14px] font-semibold text-text-primary group-hover:text-text-brand">
        {tool.label}
      </h3>
      <p className="text-[12px] leading-relaxed text-text-tertiary">
        {tool.desc}
      </p>
    </Link>
  );
}

/* ────────────── Why ────────────── */

const WHY_POINTS: Array<{ icon: LucideIcon; title: string; desc: string }> = [
  {
    icon: Database,
    title: "실시간 빅데이터",
    desc: "42,000개 키워드와 7개 채널 유형을 매일 갱신합니다.",
  },
  {
    icon: Zap,
    title: "AI 분석 자동화",
    desc: "21개 AI 기능이 키워드 분류·성장 예측·카피 생성까지 대행합니다.",
  },
  {
    icon: Shield,
    title: "검증된 신뢰성",
    desc: "2020년부터 5년간 축적된 도메인 로직, 99% 서비스 안정성.",
  },
  {
    icon: Rocket,
    title: "즉시 실행 가능",
    desc: "분석 결과를 대시보드 · 알림 · CSV로 바로 연결합니다.",
  },
];

export function WhySection() {
  return (
    <section className="border-b border-stroke-subtle py-20">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="Why datalab.tools"
          title="데이터 기반 의사결정의 표준"
          description="마케터·창작자가 매일 마주치는 분석 과제를 한 곳에서."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {WHY_POINTS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-sm border border-stroke-default bg-surface-raised p-6"
            >
              <div className="flex size-9 items-center justify-center rounded-sm bg-accent-soft text-text-brand">
                <Icon className="size-4" />
              </div>
              <h3 className="mt-4 text-[14px] font-semibold text-text-primary">
                {title}
              </h3>
              <p className="mt-2 text-[12px] leading-relaxed text-text-tertiary">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────── Proof / About ────────────── */

const PROOF_STATS: Array<{ icon: LucideIcon; val: string; label: string }> = [
  { icon: Award, val: "2020", label: "서비스 시작" },
  { icon: Users, val: "2,400+", label: "활성 사용자" },
  { icon: Database, val: "42K+", label: "분석 키워드" },
  { icon: Shield, val: "99%", label: "서비스 안정성" },
];

export function AboutSection() {
  return (
    <section
      id="about"
      className="border-b border-stroke-subtle bg-surface-sunken py-20"
    >
      <div className="mx-auto max-w-3xl px-6">
        <SectionHeading
          eyebrow="About datalab.tools"
          title="5년간 한 분야에만 집중한 솔루션"
        />
        <div className="mt-10 space-y-5 rounded-sm border border-stroke-default bg-surface-raised p-8 text-[14px] leading-[1.9] text-text-secondary">
          <p>
            데이터랩툴즈는 2020년부터 키워드 마케팅 빅데이터에만 집중해 온 분석
            솔루션입니다. 마케팅 실무 경험과 블로그·포스트·인플루언서 창작자와의
            소통을 바탕으로 지속적으로 발전해 왔습니다.
          </p>
          <p>
            2024년부터는 AI 분석 기능을 본격 도입하여 키워드 분류·검색 의도
            분석·채널 건강 진단·성장 예측 등 정교한 의사결정 근거를 제공합니다.
            5년간 쌓인 도메인 로직 위에 AI를 결합한 것이 데이터랩툴즈의
            차별점입니다.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          {PROOF_STATS.map(({ icon: Icon, val, label }) => (
            <div
              key={label}
              className="rounded-sm border border-stroke-default bg-surface-raised p-5"
            >
              <div className="flex size-8 items-center justify-center rounded-sm bg-accent-soft text-text-brand">
                <Icon className="size-4" />
              </div>
              <Numeric
                as="p"
                className="mt-3 text-[20px] font-extrabold tracking-tight text-text-primary"
              >
                {val}
              </Numeric>
              <p className="text-[11px] font-medium text-text-tertiary">
                {label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────── Pricing teaser ────────────── */

export function PricingSection() {
  return (
    <section id="pricing" className="border-b border-stroke-subtle py-20">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="Pricing"
          title="알파 버전은 무료"
          description="정식 런칭 전까지 모든 기능을 무료로 사용할 수 있습니다."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <PricingCard
            name="Alpha"
            price="무료"
            description="정식 런칭 전 모든 기능을 체험할 수 있습니다."
            features={[
              "42,000개 키워드 풀 액세스",
              "48개 분석 기능 전체 제공",
              "21개 AI 기능 사용",
              "커뮤니티 지원",
            ]}
            cta="지금 시작하기"
            ctaTo={DASHBOARD_PATH}
          />
          <PricingCard
            name="정식 버전"
            price="Coming soon"
            description="런칭 시점 업데이트 예정입니다."
            features={[
              "알파 플랜 기능 유지",
              "팀 워크스페이스",
              "사용량 기반 API",
              "우선 지원",
            ]}
            cta="런칭 알림 받기"
            ctaHref="mailto:help@myso.kr"
            dim
          />
        </div>
      </div>
    </section>
  );
}

function PricingCard({
  name,
  price,
  description,
  features,
  cta,
  ctaTo,
  ctaHref,
  dim = false,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  ctaTo?: string;
  ctaHref?: string;
  dim?: boolean;
}) {
  const ctaClass = dim
    ? "rounded-sm border border-stroke-default px-4 py-2.5 text-[13px] font-medium text-text-secondary hover:bg-surface-sunken"
    : "rounded-sm bg-accent px-4 py-2.5 text-[13px] font-semibold text-text-inverse hover:bg-accent-hover";
  return (
    <div className="flex flex-col gap-4 rounded-sm border border-stroke-default bg-surface-raised p-8">
      <div>
        <h3 className="text-[16px] font-bold text-text-primary">{name}</h3>
        <p className="mt-1 text-[24px] font-extrabold tracking-tight text-text-brand">
          {price}
        </p>
        <p className="mt-2 text-[13px] text-text-tertiary">{description}</p>
      </div>
      <ul className="space-y-2 border-t border-stroke-subtle pt-4 text-[13px] text-text-secondary">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="mt-1 inline-block size-1.5 rounded-full bg-accent" />
            {f}
          </li>
        ))}
      </ul>
      {ctaTo ? (
        <Link to={ctaTo} className={`${ctaClass} text-center`}>
          {cta}
        </Link>
      ) : (
        <a href={ctaHref} className={`${ctaClass} text-center`}>
          {cta}
        </a>
      )}
    </div>
  );
}

/* ────────────── Final CTA ────────────── */

export function CtaSection({ isAuthenticated }: { isAuthenticated: boolean }) {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-extrabold leading-tight text-text-primary">
          5년간 누적된 도메인 로직 위에 AI를 더한,
          <br />
          <span className="text-text-brand">키워드 마케팅 분석의 표준</span>
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-[14px] leading-[1.8] text-text-secondary">
          지금 바로 데이터랩툴즈를 무료로 사용해 보세요. 42,000개 키워드와 48개
          분석 기능이 기다립니다.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to={DASHBOARD_PATH}
            className="group inline-flex items-center gap-2 rounded-sm bg-accent px-6 py-3 text-[14px] font-semibold text-text-inverse shadow-sm hover:bg-accent-hover"
          >
            {isAuthenticated ? "대시보드 이동" : "무료로 시작하기"}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <a
            href="https://modootoday.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-sm border border-stroke-default px-6 py-3 text-[14px] font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
          >
            회사 소개 <ExternalLink className="size-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ────────────── Footer ────────────── */

const FOOTER_SECTIONS = [
  {
    label: "제품",
    links: [
      { label: "대시보드", href: "/dashboard" },
      { label: "키워드 분석", href: "/keywords" },
      { label: "채널 진단", href: "/channels" },
    ],
  },
  {
    label: "리소스",
    links: [
      { label: "도움말", href: "/help" },
      { label: "문의하기", href: "mailto:help@myso.kr" },
    ],
  },
  {
    label: "회사",
    links: [
      { label: "모두의오늘", href: "https://modootoday.com" },
      { label: "개인정보처리방침", href: "/privacy" },
    ],
  },
];

export function LandingFooter() {
  return (
    <div>
      <div className="grid gap-10 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2">
            <BrandLogo className="size-7" />
            <span className="text-[14px] font-bold">datalab.tools</span>
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-text-tertiary">
            모두의오늘이 만든 키워드 마케팅 분석 솔루션.
          </p>
        </div>
        {FOOTER_SECTIONS.map((section) => (
          <div key={section.label}>
            <p className="text-[12px] font-semibold uppercase tracking-wider text-text-tertiary">
              {section.label}
            </p>
            <ul className="mt-3 space-y-2 text-[13px] text-text-secondary">
              {section.links.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="hover:text-text-primary">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="mt-10 border-t border-stroke-subtle pt-6 text-center text-[11px] text-text-tertiary">
        © {new Date().getFullYear()} 모두의오늘. All rights reserved.
      </p>
    </div>
  );
}

/* ────────────── Helpers ────────────── */

function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="max-w-2xl">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-text-brand">
        {eyebrow}
      </p>
      <h2 className="mt-3 text-[clamp(1.5rem,3.5vw,2rem)] font-extrabold leading-tight text-text-primary">
        {title}
      </h2>
      {description && (
        <p className="mt-4 text-[14px] leading-relaxed text-text-secondary">
          {description}
        </p>
      )}
    </div>
  );
}
